import CartPaymentMethods from "@app-contexts/public/features/basket/components/cart-summary/CartPaymentMethods/CartPaymentMethods.web";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import useHoldDetails from "@app-contexts/user/features/basket/data/services/useHoldDetails";
import CartTimer from "@shared/components/application/data-display/CartTimer/CartTimer.web";
import CheckoutLabel from "@shared/components/application/data-display/CheckoutLabel/CheckoutLabel.web";
import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import useWindowSize from "@shared/hooks/useWindowSize";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import url from "@shared/utils/urls/url";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import Heading from "@ui-kit/typography/Heading/Heading.web";
import { useMemo } from "react";
import { useQueryClient } from "react-query";
import CartItem from "../cart-summary-items/cart-item/CartItem/CartItem.web";
import { CartSummaryProps } from "./CartSummary.props";

const { sm } = breakpoints;

const CartSummary = ({
  venue,
  holdLink = undefined,
  isDrawer,
  onCartClose = undefined,
  dynamicHoldParams = undefined,
}: CartSummaryProps) => {
  const { t } = useTranslation("purchases");

  const {
    isBasketUpdating,
    itemGroups,
    supportedCCTypes,
    totalItemQuantity,
    isWaitlist,
    basketItemData,
    basketUpgrades,
  } = usePublicBasket({
    enableItemData: true,
  });

  const hasItems = (itemGroups?.length ?? 0) > 0 ?? false;

  const queryClient = useQueryClient();
  const { data: holdDetailsData } = useHoldDetails(
    holdLink || "",
    dynamicHoldParams,
    {
      enabled: !!holdLink,
      staleTime: 60 * 1000,
      initialData: () => {
        return queryClient.getQueryData(["holdDetails", holdLink]);
      },
    }
  );

  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => windowWidth && windowWidth < sm - 1,
    [windowWidth]
  );

  const basketUpgradesData = basketUpgrades.getBasketUpgrades();
  const disableCheckout = !hasItems;

  const displayItemGroups = useMemo(() => {
    return itemGroups.filter(
      (item) => item.item_type !== ITEM_TYPES_MAP.ITEM_TYPE_PROTECTION.id
    );
  }, [itemGroups]);

  return (
    <Flex
      flexDirection="column"
      {...(isDrawer
        ? { height: "100%" }
        : // 445px is the height of the header, used to prevent cart summary from extending below page.
          { maxHeight: "calc(100vh - 445px)" })}
      overflow="hidden"
      marginInlineStart="none"
    >
      <Box flex="1" overflowY="auto" minHeight="none" overflowX="hidden">
        {!isDrawer && (
          <Heading size="2xl" color="fg">
            {t("Cart")}
          </Heading>
        )}

        {!isSmallerThanSm && (
          <Box my="xs">
            <CartTimer.Text />
          </Box>
        )}

        <Box w="full" mt="xs" mb="xl">
          {displayItemGroups.map((item) => (
            <CartItem
              key={item.id}
              item={item}
              disabled={isBasketUpdating}
              venue={venue}
              event={basketItemData.eventsMapping[item.event]}
              product={basketItemData.productsMapping[item.product]}
              ticketType={
                basketItemData.ticketTypesMapping[`${item.event}:${item.type}`]
              }
              productAttribute={
                basketItemData.productsAttributesMapping[
                  `${item.product}:${item.product_attribute}`
                ]
              }
              membershipLevel={
                basketItemData.membershipLevelsMapping[
                  `${item.membership_group}:${item.membership_level}`
                ]
              }
              onCartClose={onCartClose}
              holdPurchaseLimits={{
                min: holdDetailsData?.on_hold_detail?.purchase_limit_min,
                max: holdDetailsData?.on_hold_detail?.purchase_limit_max,
              }}
              itemGroupUpgrades={
                item.id ? basketUpgradesData?.[item.id] : undefined
              }
            />
          ))}
        </Box>
      </Box>

      {!isDrawer && (
        <Box flexShrink={0}>
          <Link
            mt="lg"
            role="button"
            tabIndex={0}
            href={disableCheckout ? undefined : url("checkout")}
            _hover={{ textDecoration: "none" }}
          >
            <Button
              data-testid="detail-page-cart-checkout"
              colorPalette="primary"
              variant="solid"
              width="100%"
              minHeight="element.3xl"
              disabled={disableCheckout || isBasketUpdating}
              isLoading={isBasketUpdating}
            >
              <CheckoutLabel
                totalItemQuantity={totalItemQuantity}
                isWaitlist={isWaitlist}
              />
            </Button>
          </Link>

          {supportedCCTypes && supportedCCTypes.length > 0 && (
            <>
              <Divider my="lg" />
              <Box w="full">
                <CartPaymentMethods supportedCardTypes={supportedCCTypes} />
              </Box>
            </>
          )}
        </Box>
      )}
    </Flex>
  );
};

export default CartSummary;
